import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Input,
  LoadingIcon,
  Modal,
  Selector,
  Tooltip,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';
import { alphaSorter } from 'vet-bones/utils';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  ADD_MY_ORG_ENDPOINT_DIALOG_CANCEL,
  ADD_MY_ORG_ENDPOINT_DIALOG_ENDPOINT,
  ADD_MY_ORG_ENDPOINT_DIALOG_ENDPOINT_ITEM,
  ADD_MY_ORG_ENDPOINT_DIALOG_NAME,
  ADD_MY_ORG_ENDPOINT_DIALOG_PASSWORD,
  ADD_MY_ORG_ENDPOINT_DIALOG_SUBMIT,
  ADD_MY_ORG_ENDPOINT_DIALOG_USERNAME,
} from 'src/ui/constants/testIds';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import { ConnectionStatus, clearConnection } from 'src/ui/features/connection';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useAddMyOrganizationConnectionMutation,
  useListOrganizationConnectionsQuery,
} from 'src/ui/graph/types';
import { useVerifyConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(
  DialogType.ADD_MY_ORGANIZATION_ENDPOINT
);

export const AddMyOrgEndpointDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const isOpen = useAppSelector(isOpenSelector);

  const invalidateConnections = useInvalidateConnections(org);
  const { data: connectionsData } = useListOrganizationConnectionsQuery(
    graphQLClient,
    { org_domain: org },
    { enabled: Boolean(org) }
  );

  const {
    isLoading: isValidating,
    mutate: validate,
    reset: resetValidation,
  } = useVerifyConnection();
  const {
    isLoading: isAdding,
    mutate: addConnection,
  } = useAddMyOrganizationConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: () => {
      invalidateConnections();
      dispatch(closedDialog());
      dispatch(clearConnection());
    },
  });

  const endpointItems = React.useMemo(() => {
    const urls = new Set<string>();
    connectionsData?.listConnections?.forEach((conn) => {
      if (!conn) {
        return;
      }
      urls.add(conn.endpoint);
    });
    return Array.from(urls)
      .sort(alphaSorter)
      .map((url) => ({ value: url, text: url }));
  }, [connectionsData]);

  const [endpoint, setEndpoint] = React.useState('');
  const [name, setName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');

  const handleClose = () => {
    dispatch(closedDialog());
    resetValidation();
    setEndpoint('');
    setName('');
    setUsername('');
    setPassword('');
  };

  const handleSubmit = () => {
    const trimmedEndpoint = endpoint.trim();
    const trimmedName = name.trim();
    const trimmedUsername = username.trim();

    if (
      !trimmedEndpoint ||
      !trimmedName ||
      (trimmedUsername && !password) ||
      (!trimmedUsername && password)
    ) {
      return;
    }

    // Test connection with new password, it is important that we remove the
    // existing token if there is one because the Stardog.js client will prefer
    // using that and we'll just get a 401 making the user super mad and throw
    // stuff. We don't want to be responsible for broken screens or coffee mugs.
    const newConnection = {
      endpoint: trimmedEndpoint,
      token: undefined,
      username: trimmedUsername,
      password,
      useSSO: false,
      name: trimmedName,
      status: ConnectionStatus.DISCONNECTED,
    };

    validate(newConnection, {
      onError,
      onSuccess: (data) => {
        invalidateConnections();
        addConnection({
          input: {
            endpoint: trimmedEndpoint,
            name: trimmedName,
            org_domain: org,
            token: data.token,
            username: trimmedUsername,
          },
        });
      },
    });
  };

  let submitTooltip = '';
  if (!name.trim()) {
    submitTooltip = copy.components.endpoints.inputs.name.validationRequired;
  } else if (!endpoint.trim()) {
    submitTooltip =
      copy.components.endpoints.inputs.endpoint.validationRequired;
  } else if (username && !password) {
    submitTooltip =
      copy.components.endpoints.inputs.password.validationRequiredWithUsername;
  } else if (!username && password) {
    submitTooltip =
      copy.components.endpoints.inputs.username.validationRequired;
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={ADD_MY_ORG_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.addMyOrgEndpointDialog.buttons.cancel}
          </Button>
          <Tooltip description={submitTooltip} disabled={!submitTooltip}>
            <Button
              disabled={isValidating || isAdding || Boolean(submitTooltip)}
              leftIcon={isValidating || isAdding ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={ADD_MY_ORG_ENDPOINT_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.endpoints.addMyOrgEndpointDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.endpoints.addMyOrgEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            autoFocus
            className="endpoint-dialog-input-help-icon-hidden"
            clearable={false}
            helpProps={{ text: copy.components.endpoints.inputs.name.help }}
            id="sd-connection-name"
            label={copy.components.endpoints.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            required
            size="md"
            testId={ADD_MY_ORG_ENDPOINT_DIALOG_NAME}
            value={name}
          />

          <Selector.Single<null, SelectItem, null | SelectItem>
            filterable
            inputProps={{
              className: 'endpoint-dialog-input-selector',
              label: copy.components.endpoints.inputs.endpoint.label,
              required: true,
              size: 'md',
              testId: ADD_MY_ORG_ENDPOINT_DIALOG_ENDPOINT,
              value: endpoint,
            }}
            items={endpointItems}
            itemTestId={ADD_MY_ORG_ENDPOINT_DIALOG_ENDPOINT_ITEM}
            onSelect={(selected) => setEndpoint(selected?.value || '')}
            popoverProps={{
              portalClassName: 'endpoint-dialog-menu-portal',
            }}
            selected={
              endpointItems.find((item) => item.value === endpoint) || null
            }
          />

          <Input
            autoComplete="username"
            clearable={false}
            id="sd-connection-username"
            label={copy.components.endpoints.inputs.username.label}
            onChange={(e) => setUsername(e.target.value)}
            size="md"
            testId={ADD_MY_ORG_ENDPOINT_DIALOG_USERNAME}
            value={username}
          />

          <Input
            autoComplete="password"
            clearable={false}
            id="sd-connection-password"
            label={copy.components.endpoints.inputs.password.label}
            onChange={(e) => setPassword(e.target.value)}
            size="md"
            testId={ADD_MY_ORG_ENDPOINT_DIALOG_PASSWORD}
            type="password"
            value={password}
          />

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
